import { useState } from "react";
import { useNavigate } from "react-router-dom";
import WorkspaceShell from "@/components/WorkspaceShell";
import ChatPane from "@/components/ChatPane";
import { productByKey } from "@/lib/products";
import api from "@/lib/api";
import { toast } from "sonner";
import { handleQuotaError } from "@/lib/quota";
import { Loader2, Wand2, FileText, MessageSquare, Atom, Sliders, Activity, ClipboardCheck, FileCheck } from "lucide-react";
import { ParametricPanel, DFMPanel, FEAPanel, PPAPPanel } from "@/components/sufi/SufiPanels";

const PRODUCT = productByKey("sufi");

const STARTERS = [
  "Optimize a steel mounting bracket for 50% weight reduction under 5kN load, factor of safety 2.5.",
  "Design a turbine blade for 12% efficiency gain at 15,000 RPM operating temp 850°C.",
  "Inverse-design a heat exchanger fin pattern minimizing pressure drop for a 200W cooling load.",
  "Suggest topology for an EV battery pack tray, target stiffness 8000 N/mm, weight under 14 kg.",
];

export default function Sufi() {
  const [tab, setTab] = useState("design");
  const [req, setReq] = useState("");
  const [constraints, setConstraints] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [bump, setBump] = useState(0);
  const nav = useNavigate();

  const run = async () => {
    if (!req.trim() || loading) return;
    setLoading(true);
    try {
      const r = await api.post("/sufi/design", { requirement: req, constraints });
      setResult(r.data);
      setBump((n) => n + 1);
      toast.success("Design generated");
    } catch (e) {
      if (!handleQuotaError(e, nav)) {
        toast.error(e?.response?.data?.detail || "Design failed");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <WorkspaceShell product={PRODUCT} usageRefresh={bump}>
      <div className="flex items-center gap-2 mb-5">
        <TabButton active={tab === "design"} onClick={() => setTab("design")} icon={Wand2} label="Inverse design" testid="sufi-tab-design" />
        <TabButton active={tab === "parametric"} onClick={() => setTab("parametric")} icon={Sliders} label="Parametric" testid="sufi-tab-parametric" />
        <TabButton active={tab === "dfm"} onClick={() => setTab("dfm")} icon={ClipboardCheck} label="DFM checks" testid="sufi-tab-dfm" />
        <TabButton active={tab === "fea"} onClick={() => setTab("fea")} icon={Activity} label="FEA preview" testid="sufi-tab-fea" />
        <TabButton active={tab === "ppap"} onClick={() => setTab("ppap")} icon={FileCheck} label="PPAP" testid="sufi-tab-ppap" />
        <TabButton active={tab === "chat"} onClick={() => setTab("chat")} icon={MessageSquare} label="CAE co-pilot" testid="sufi-tab-chat" />
      </div>

      {tab === "design" && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <aside className="lg:col-span-4 space-y-4">
            <div className="rounded-sm border border-border/60 bg-card p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Performance requirement</div>
              <textarea
                data-testid="sufi-req"
                value={req}
                onChange={(e) => setReq(e.target.value)}
                rows={4}
                placeholder="State your performance target…"
                className="w-full resize-none rounded-sm border border-border bg-background p-3 text-sm outline-none focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10"
              />
              <div className="mt-3">
                <div className="text-xs uppercase tracking-[0.16em] text-muted-foreground mb-1.5">Constraints</div>
                <textarea
                  data-testid="sufi-constraints"
                  value={constraints}
                  onChange={(e) => setConstraints(e.target.value)}
                  rows={3}
                  placeholder="Material, manufacturing process, cost ceiling…"
                  className="w-full resize-none rounded-sm border border-border bg-background p-3 text-sm outline-none focus:border-foreground/40 focus:ring-2 focus:ring-foreground/10"
                />
              </div>
              <button
                data-testid="sufi-generate"
                onClick={run}
                disabled={loading || !req.trim()}
                className="mt-5 inline-flex w-full h-11 items-center justify-center gap-2 rounded-sm text-white text-sm font-medium disabled:opacity-50"
                style={{ background: PRODUCT.gradient }}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
                {loading ? "Optimizing…" : "Run inverse design"}
              </button>
            </div>

            <div className="rounded-sm border border-border/60 bg-card p-5">
              <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Try an example</div>
              <div className="space-y-1.5">
                {STARTERS.map((s, i) => (
                  <button
                    key={i}
                    data-testid={`sufi-starter-${i}`}
                    onClick={() => setReq(s)}
                    className="block w-full text-start text-xs rounded-sm bg-secondary/60 hover:bg-secondary px-3 py-2 transition-colors"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          </aside>

          <section className="lg:col-span-8">
            <div className="grid grid-cols-1 gap-4">
              <div className="rounded-sm border border-border/60 bg-card overflow-hidden">
                <div className="px-5 py-3 border-b border-border/60 flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
                  <Atom className="h-3.5 w-3.5" /> Optimized geometry
                </div>
                <div className="aspect-video bg-secondary/40 flex items-center justify-center relative">
                  {loading && (
                    <div className="absolute inset-0 flex items-center justify-center bg-card/70 backdrop-blur-sm z-10">
                      <div className="flex items-center gap-3 text-sm text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" /> Running FEA loop…
                      </div>
                    </div>
                  )}
                  {result?.geometry_b64 ? (
                    <img
                      src={`data:image/png;base64,${result.geometry_b64}`}
                      alt="Optimized geometry"
                      className="max-w-full max-h-full object-contain"
                    />
                  ) : (
                    <div className="text-sm text-muted-foreground p-8 text-center">
                      Define a requirement to generate optimized geometry.
                    </div>
                  )}
                </div>
              </div>

              {result && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="rounded-sm border border-border/60 bg-card p-5">
                    <div className="text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">Design summary</div>
                    <p className="text-sm leading-relaxed">{result.design_summary}</p>
                    <div className="mt-5 text-xs uppercase tracking-[0.18em] text-muted-foreground mb-2">Optimization steps</div>
                    <ol className="space-y-1.5">
                      {result.optimization_steps.map((s, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm">
                          <span
                            className="mt-0.5 h-5 w-5 shrink-0 rounded-sm text-[11px] font-mono flex items-center justify-center text-white"
                            style={{ background: PRODUCT.gradient }}
                          >
                            {i + 1}
                          </span>
                          {s}
                        </li>
                      ))}
                    </ol>
                  </div>

                  <div className="rounded-sm border border-border/60 bg-card p-5 flex flex-col">
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground mb-3">
                      <FileText className="h-3.5 w-3.5" /> PPAP document (draft)
                    </div>
                    <pre className="flex-1 text-[12px] font-mono whitespace-pre-wrap leading-relaxed text-muted-foreground">
                      {result.ppap_doc}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </section>
        </div>
      )}

      {tab === "parametric" && <ParametricPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}
      {tab === "dfm" && <DFMPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}
      {tab === "fea" && <FEAPanel accent={PRODUCT.accent} />}
      {tab === "ppap" && <PPAPPanel accent={PRODUCT.accent} gradient={PRODUCT.gradient} />}

      {tab === "chat" && (
        <ChatPane
          product={PRODUCT}
          endpoint="/sufi/chat"
          accent={PRODUCT.accent}
          placeholder="Describe a simulation problem…"
          starters={STARTERS}
          stream
          streamProduct="sufi"
          onSent={() => setBump((n) => n + 1)}
        />
      )}
    </WorkspaceShell>
  );
}

function TabButton({ active, onClick, icon: Icon, label, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex h-9 items-center gap-2 rounded-sm px-3.5 text-sm font-medium transition-colors ${
        active
          ? "bg-foreground text-background"
          : "bg-card border border-border/60 text-muted-foreground hover:text-foreground"
      }`}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </button>
  );
}
